import { useCallback, useEffect, useRef, useState } from 'react'
import { annonceAutomate, type DonneesMeteo } from '../../api/meteo'
import { chargerMeteo, suivreMeteo } from '../../api/meteoAcces'

/**
 * État de la météo automatique, tenu à jour en temps réel.
 *
 * `surBascule` est appelé quand un épisode commence ou finit : c'est le signe que
 * `platform_config.meteo_actuelle` a changé (la configuration est à relire).
 * `annonce` porte la dernière décision de l'AUTOMATE, pour le bandeau.
 */
export function useMeteo(surBascule?: () => void) {
  const [donnees, setDonnees] = useState<DonneesMeteo | null>(null)
  /** true = la base n'a pas encore la migration 0061. */
  const [absente, setAbsente] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [annonce, setAnnonce] = useState<string | null>(null)
  const precedent = useRef<DonneesMeteo | null>(null)
  const rappel = useRef(surBascule)
  rappel.current = surBascule

  const recharger = useCallback(async () => {
    try {
      const d = await chargerMeteo()
      if (!d) {
        setAbsente(true)
        return
      }
      const a = annonceAutomate(precedent.current, d)
      if (a) setAnnonce(a)
      precedent.current = d
      setDonnees(d)
      setAbsente(false)
      setErreur(null)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Lecture de la météo impossible.')
    }
  }, [])

  useEffect(() => {
    void recharger()
    const arreter = suivreMeteo((table) => {
      void recharger()
      if (table === 'meteo_episodes') rappel.current?.()
    })
    /* Filet si le temps réel décroche : un relevé a lieu toutes les 10 min. */
    const minuterie = setInterval(() => void recharger(), 120_000)
    return () => {
      arreter()
      clearInterval(minuterie)
    }
  }, [recharger])

  return { donnees, absente, erreur, annonce, effacerAnnonce: () => setAnnonce(null), recharger }
}
